<script setup lang="ts">
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useUserMutation } from '@/api/mutations/user/useUserMutation';
import { useLanguageList } from '@/hooks/useLanguageList';
import { useLocaleStore } from '@/stores/locales';

const localeStore = useLocaleStore();

const { currentLocale, loading, currentLang: currentLang } = storeToRefs(localeStore);
const itemIsActive = (id: number) => currentLocale?.value.id === id;

const selection = ref(currentLocale.value.id);
const { updateUser: updateLocale } = useUserMutation(
  computed(() => ({ locale_id: selection.value })),
);

const { languagesList } = useLanguageList();

const activeStyle = {
  border: '4px solid rgb(var(--v-theme-btnHighlightedBorder))',
  background: 'rgb(var(--v-theme-btnHighlightedBackground))',
  color: 'rgb(var(--v-theme-btnHighlightedColor))',
};

function updateAuthLocale(localeId: number) {
  selection.value = localeId;
  updateLocale();
}
</script>

<template>
  <div class="d-flex justify-start align-center">
    <v-menu
      max-height="300"
      location="bottom end"
      activator="parent"
      :offset="8"
      transition="slide-y-transition"
    >
      <template #activator="{ isActive, props }">
        <v-btn
          :class="[
            'text-body-2 text-uppercase focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 transition-colors focus-visible:outline-blue-600',
            'hover:bg-[rgb(var(--v-theme-btnHighlightedBackground))] hover:text-[rgb(var(--v-theme-btnHighlightedColor))]',
            isActive &&
              'bg-[rgb(var(--v-theme-btnHighlightedBackground))] text-[rgb(var(--v-theme-btnHighlightedColor))] border-[4px] border-[rgb(var(--v-theme-btnHighlightedBorder))]',
          ]"
          v-bind="props"
          :height="44"
          :style="isActive ? activeStyle : undefined"
          elevation="0"
        >
          <v-icon class="font-icon-2 me-2" icon="fas fa-earth-americas" />
          <v-progress-circular
            v-if="loading"
            color="primary"
            indeterminate
            size="small"
          ></v-progress-circular>
          <span v-else>{{ currentLang }}</span>
        </v-btn>
      </template>
      <v-list density="compact" class="pa-0 rounded-t-0 text-body-3">
        <v-list-item
          v-for="(item, i) in languagesList"
          :key="i"
          :value="item"
          color="contrast"
          :active="itemIsActive(item.id)"
          @click="updateAuthLocale(item.id)"
        >
          {{ item.title }}
          <template v-if="itemIsActive(item.id)" #append>
            <v-icon icon="fat fa-check" class="font-icon-2"></v-icon>
          </template>
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>
